<template>
    <shield-modal
        :id="'edit-information-modal'"
        class="edit-information-modal"
        :size="'md'"
        @handleSubmit="save"
        @handleCancel="hideModal"
        :enableSubmit="isValid"
        :noClose="false">
        <template #title>
            Edit Home Details
        </template>
        <template #default>
            <p>
              If you make changes to your address after signing your forms, you and any additional sellers will have to re-sign the forms before downloading them. 
            </p>
            <b-form @submit.prevent="save" :enableSubmit="isValid" >
                <h2>
                  Seller Information
                </h2>
                <hr />

                <b-row class="mt-2">
                  <b-col cols="12" md="6">
                    <b-form-group>
                      <label class="form-labels">First Name</label>
                      <b-form-input id="firstName"
                        v-model="firstName" @input="validate()"
                        class="form-input"/>
                        <b-form-invalid-feedback :state="validation.firstName">
                            {{ "First Name is required" }}
                        </b-form-invalid-feedback>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6">
                    <b-form-group>
                      <label class="form-labels">Last Name</label>
                      <b-form-input id="lastName"
                        v-model="lastName" @input="validate()"
                        class="form-input"/>
                        <b-form-invalid-feedback :state="validation.lastName">
                            {{ "Last Name is required" }}
                        </b-form-invalid-feedback>
                    </b-form-group>
                  </b-col>
                </b-row>

                <h2>
                  Property Information
                </h2>
                <hr />

                <b-row class="mt-2">
                  <b-col cols="12">
                    <b-form-group>
                      <label class="form-labels">Street Address</label>
                      <b-form-input id="street-address"
                        v-model="streetAddress" @input="validate()"
                        class="form-input"/>
                        <b-form-invalid-feedback :state="validation.streetAddress">
                            {{ "Street Address is required" }}
                        </b-form-invalid-feedback>                        
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="12" md="6">
                    <b-form-group>
                      <label class="form-labels">City</label>
                      <b-form-input id="city"
                        v-model="city" @input="validate()"
                        class="form-input"/>
                        <b-form-invalid-feedback :state="validation.city">
                            {{ "City is required" }}
                        </b-form-invalid-feedback>                        
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6">
                    <b-form-group>
                      <label class="form-labels">Zip Code</label>
                      <b-form-input id="zip"
                        v-model="zip" @input="validate()"
                        class="form-input"/>
                        <b-form-invalid-feedback :state="validation.zip">
                            {{ "Zip Code is required" }}
                        </b-form-invalid-feedback>                        
                    </b-form-group>
                  </b-col>
                </b-row>

                <template v-if="hasSecondarySeller">
                  <h2>
                    Additional Seller
                  </h2>
                  <hr />
                  <b-row class="mt-2">
                    <b-col cols="12" md="6">
                      <b-form-group>
                        <label class="form-labels">First Name</label>
                        <b-form-input id="secondaryFirstName"
                          v-model="secondaryFirstName" @input="validate()"
                          class="form-input"/>
                          <b-form-invalid-feedback :state="validation.secondaryFirstName">
                            {{ "First Name is required" }}
                        </b-form-invalid-feedback>                          
                      </b-form-group>
                    </b-col>
                    <b-col cols="12" md="6">
                      <b-form-group>
                        <label class="form-labels">Last Name</label>
                        <b-form-input id="secondaryLastName"
                          v-model="secondaryLastName" @input="validate()"
                          class="form-input"/>
                          <b-form-invalid-feedback :state="validation.secondaryLastName">
                            {{ "Last Name is required" }}
                        </b-form-invalid-feedback>                          
                      </b-form-group>
                    </b-col>
                  </b-row>
                </template>
            </b-form>
        </template>
        <template #submit-btn>
            Save Property Information
        </template>
        
        <template #cancel-btn>
            Cancel
        </template>
    </shield-modal>
</template>
<script>
import './EditInformationModal.scss';
import ShieldModal from '@components/modals/ShieldModal/ShieldModal.vue';
import {primarySeller, secondarySeller, propertyStreetAddress, propertyCity, propertyState} from '@helpers/order';
import {cloneDeep, get} from 'lodash';
import OrderAppResource from "@sellers-shield-home-protection/ss-core-js/src/AppResources/OrderAppResource";
import SellerAppResource from '@sellers-shield-home-protection/ss-core-js/src/AppResources/SellerAppResource';
import OrderPropertyAppResource from '@sellers-shield-home-protection/ss-core-js/src/AppResources/OrderPropertyAppResource';
import FormErrorsMixin from '@sellers-shield-home-protection/ss-ui-kit/src/components/FormErrors/FormErrorsMixin';

export default {
    name:'EditInformationModal',
    props: {
        orderAppResource:{
            type: Object,
            required: true,
        }
    },
    mixins:[
        FormErrorsMixin 
    ],
    data: () => ({
      firstName: '',
      lastName: '',
      streetAddress: '',
      city: '',
      zip: '',
      secondaryFirstName: '',
      secondaryLastName: '',
      validation: {
        firstName: true,
        lastName: true,
        streetAddress: true,
        city: true,
        zip: true,
        secondaryFirstName: true,
        secondaryLastName: true,
      },
      saving: false
    }),
    watch: {
        orderAppResource: {
          immediate: true,
          async handler() {
              
              if(this.primarySeller){
                this.firstName = this.primarySeller.firstName;
                this.lastName = this.primarySeller.lastName;
              }
              if(this.property){
                this.streetAddress = this.property.streetAddress;
                this.city = this.property.city;
                this.zip = this.property.zip;
              }
              if(this.secondarySeller){
                this.secondaryFirstName = this.secondarySeller.firstName;
                this.secondaryLastName = this.secondarySeller.lastName;
              }
              this.validate();
          },
        },
    },    
    components:{
        ShieldModal
    },
    methods:{
        hideModal(){
            this.$bvModal.hide('edit-information-modal');
        },
        validate() {
            this.$set(this.validation, 'firstName', !!this.firstName ); // Always true to allow empty fields
            this.$set(this.validation, 'lastName', !!this.lastName);
            this.$set(this.validation, 'streetAddress', !!this.streetAddress);
            this.$set(this.validation, 'city', !!this.city);
            this.$set(this.validation, 'zip', !!this.zip);
            this.$set(this.validation, 'secondaryFirstName', (this.hasSecondarySeller && !!this.secondaryFirstName) || !this.hasSecondarySeller);
            this.$set(this.validation, 'secondaryLastName', (this.hasSecondarySeller && !!this.secondaryLastName) || !this.hasSecondarySeller);
        },
        async saveProperty(){
          if(this.property){
            let orderProperty = cloneDeep(this.property);
            orderProperty.setAttribute('streetAddress', this.streetAddress);
            orderProperty.setAttribute('city', this.city);
            orderProperty.setAttribute('zip', this.zip);
            orderProperty.setAttribute('orderUuid', this.orderAppResource.uuid);
            orderProperty.setAttribute('stateId', this.property.stateId);
            await orderProperty.save(true);
            }
        },
        async saveSellerInfo() {
            if(this.primarySeller){
              let pSeller = cloneDeep(this.primarySeller);
              pSeller.setAttribute('firstName', this.firstName);
              pSeller.setAttribute('lastName', this.lastName);
              pSeller.setAttribute('email', this.primarySeller.email);
              pSeller.setAttribute('phone', this.primarySeller.phone);
              pSeller.setAttribute('type', this.primarySeller.type);
              pSeller.setAttribute('userUuid', this.primarySeller.userUuid);
              await pSeller.save(true);
            }

            if(this.hasSecondarySeller && this.secondarySeller){
              let sSeller = cloneDeep(this.secondarySeller);
              sSeller.setAttribute('firstName', this.secondaryFirstName);
              sSeller.setAttribute('lastName', this.secondaryLastName);
              sSeller.setAttribute('email', this.secondarySeller.email);
              sSeller.setAttribute('phone', this.secondarySeller.phone);
              sSeller.setAttribute('type', this.secondarySeller.type);
              sSeller.setAttribute('userUuid', this.secondarySeller.userUuid);              
              await sSeller.save(true);
            }
        },
        async save() {
            this.validate();
            if (this.isValid) {
                await this.saveProperty();
                await this.saveSellerInfo();
                this.$emit('save',{
                  city: this.city,
                  address: this.streetAddress,
                  zipCode: this.zip,
                  firstName: this.firstName,
                  lastName: this.lastName,
                  secondaryFirstName: this.secondaryFirstName,
                  secondaryLastName: this.secondaryLastName,
                });
                this.hideModal();
            }
        },        
    },
    computed:{
      isValid() {
        return Object.values(this.validation).every(isValid => isValid);
      },
      orderProperty() {
        return get(this.orderAppResource, 'property');
      },
      SellerInfo(){
        return get(this.orderAppResource, 'sellers');
      },
      primarySeller(){
        return primarySeller(this.orderAppResource);
      },
      secondarySeller(){
        return secondarySeller(this.orderAppResource);
      },
      property(){
        return cloneDeep(this.orderProperty) ?? null;
      },
      hasSecondarySeller(){
        return Object.keys(this.secondarySeller).length > 0;
      }
    },    
}
</script>
